// src/ui/InstrumentPicker.js
// Renders built-in instruments grouped by family, plus custom instruments
// in a "Custom" section at the bottom.

import { INSTRUMENT_CATALOG, getInstrumentName, getInstrumentFamily } from '../audio/instruments.js';
import { onLocaleChange } from '../i18n.js';

export const INSTRUMENT_DRAG_MIME = 'application/x-helix-instrument';

export class InstrumentPicker {
  /**
   * @param {HTMLElement} rootEl
   * @param {object} opts
   * @param {function} [opts.onSelect]
   * @param {function} [opts.onPreview]
   * @param {import('../instruments/CustomInstrumentRegistry.js').CustomInstrumentRegistry} [opts.customRegistry]
   * @param {function} [opts.onAddCustom] - called when user clicks "+ New" custom instrument
   */
  constructor(rootEl, opts = {}) {
    this.root = rootEl;
    this.onSelect = opts.onSelect;
    this.onPreview = opts.onPreview;
    this.customRegistry = opts.customRegistry || null;
    this.onAddCustom = opts.onAddCustom;

    this.render();
    onLocaleChange(() => this.render());

    // Re-render when custom instruments change
    if (this.customRegistry) {
      this._unsub = this.customRegistry.onChange(() => this.render());
    }
  }

  render() {
    this.root.innerHTML = '';
    const families = groupByFamily(INSTRUMENT_CATALOG);

    // Built-in instrument groups
    for (const [family, entries] of families) {
      const group = document.createElement('div');
      group.className = 'instrument-group';
      const heading = document.createElement('h5');
      heading.textContent = getInstrumentFamily(entries[0]);
      group.appendChild(heading);
      const grid = document.createElement('div');
      grid.className = 'instrument-grid';
      for (const entry of entries) {
        grid.appendChild(this._createTile(entry));
      }
      group.appendChild(grid);
      this.root.appendChild(group);
    }

    // Custom instrument section
    if (this.customRegistry) {
      const group = document.createElement('div');
      group.className = 'instrument-group';

      const heading = document.createElement('h5');
      heading.textContent = 'Custom';
      group.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'instrument-grid';

      // Existing custom instrument tiles
      for (const entry of this.customRegistry.getAll()) {
        grid.appendChild(this._createTile({
          ...entry,
          type: 'custom',
          family: 'Custom'
        }));
      }

      // "+ New" button
      const addBtn = document.createElement('button');
      addBtn.className = 'instrument-tile instrument-tile-add';
      addBtn.innerHTML = '<span class="tile-name">+ New</span><span class="tile-family">Create custom instrument</span>';
      addBtn.addEventListener('click', () => {
        if (this.onAddCustom) this.onAddCustom();
      });
      grid.appendChild(addBtn);

      group.appendChild(grid);
      this.root.appendChild(group);
    }
  }

  _createTile(entry) {
    const btn = document.createElement('button');
    btn.className = 'instrument-tile';
    btn.draggable = true;
    btn.style.setProperty('--tile-color', entry.color || '#888');
    const name = getInstrumentName(entry);
    const fam = getInstrumentFamily(entry) || 'Custom';
    btn.innerHTML = `
      <span class="tile-name">${name}</span>
      <span class="tile-family">${fam}</span>
    `;
    btn.title = `Click to add ${name} track`;
    btn.addEventListener('click', () => {
      if (this.onSelect) this.onSelect(entry);
    });
    if (this.onPreview) {
      btn.addEventListener('dblclick', () => this.onPreview(entry));
    }
    btn.addEventListener('dragstart', (e) => {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData(INSTRUMENT_DRAG_MIME, entry.id);
      e.dataTransfer.setData('text/plain', name);
      btn.classList.add('dragging');
    });
    btn.addEventListener('dragend', () => btn.classList.remove('dragging'));
    return btn;
  }
}

function groupByFamily(catalog) {
  const order = ['Strings', 'Winds', 'Plucked', 'Chinese'];
  const map = new Map();
  for (const e of catalog) {
    if (!map.has(e.family)) map.set(e.family, []);
    map.get(e.family).push(e);
  }
  return [...map.entries()].sort((a, b) => {
    const ia = order.indexOf(a[0]);
    const ib = order.indexOf(b[0]);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
}
